import { DownloadSimpleIcon, TrashIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import { useEffect, useRef, useState, type ChangeEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api/error';
import { toast } from '@/lib/toast';
// Download comes from the unified view surface, not the maker one: this component renders on
// the shared detail page for Checkers / Approvers / Site Heads too, and the maker route is
// owner-gated (403 for every non-owner). Upload and delete stay maker-scoped — correctly so.
import { downloadLogsheetDocumentRequest } from '@/services/logsheet-view';
import {
  deleteLogsheetDocumentRequest,
  uploadLogsheetDocumentRequest,
} from '@/services/logsheets-maker';
import type { LogsheetDocument, LogsheetStatus } from '@/types/logsheets-maker';

/**
 * Plan 12-03 — DocumentUploadZone
 *
 * Drag-and-drop / file-picker zone with client-side MIME + size validation
 * matching the backend allowlist (T-12-15 / T-12-25):
 *  - Allowed MIME: application/pdf, image/jpeg, image/png
 *  - Max size:    10 MiB
 *
 * Shows progress while uploading; lists already-uploaded documents with
 * Download + Remove buttons (Remove only when status === 'Draft' per D-111).
 *
 * When `entryId === null` (wizard step-2 pre-save) the upload zone renders
 * a disabled overlay prompting the user to save the draft first. The plan
 * (D-105) authoritatively defers pre-save document upload to v1.1+.
 */

import {
  ALLOWED_MIMES,
  formatBytes,
  validateStagedFile,
} from '@/components/logsheets-maker/stagedFileValidation';

function downloadBlob(blob: Blob, filename: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface DocumentUploadZoneProps {
  readonly entryId: number | null;
  readonly status: LogsheetStatus;
  readonly documents: readonly LogsheetDocument[];
  readonly onUploadComplete: () => void;
  readonly disabled?: boolean;
}

export function DocumentUploadZone({
  entryId,
  status,
  documents,
  onUploadComplete,
  disabled = false,
}: DocumentUploadZoneProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  // REVIEW-2 NF-4: track the in-flight upload so the user can cancel it
  // and so we abort on unmount (mirrors FF-012 bulk-wizard pattern).
  const abortRef = useRef<AbortController | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [busy, setBusy] = useState<boolean>(false);

  const canRemove = status === 'Draft' && !disabled;
  const canUpload = entryId !== null && status === 'Draft' && !disabled;

  // REVIEW-2 NF-4: abort any in-flight upload when the component unmounts so
  // we don't leave a request running against the server after the user has
  // navigated away.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  function handleCancelUpload(): void {
    abortRef.current?.abort();
  }

  async function handleFiles(files: FileList | null): Promise<void> {
    setError(null);
    if (files === null || files.length === 0 || entryId === null) return;
    const list = Array.from(files);
    for (const file of list) {
      const err = validateStagedFile(file);
      if (err !== null) {
        setError(err);
        return;
      }
    }
    // REVIEW-2 NF-4: one fresh AbortController per upload session so the
    // user's Cancel button drives a clean abort and any error envelopes
    // surface as a cancellation rather than a generic failure.
    const controller = new AbortController();
    abortRef.current = controller;
    setBusy(true);
    try {
      for (const file of list) {
        setProgress(0);
        await uploadLogsheetDocumentRequest(
          entryId,
          file,
          (pct) => {
            setProgress(pct);
          },
          controller.signal,
        );
      }
      setProgress(100);
      toast.success(
        list.length === 1 ? 'Document uploaded' : `${String(list.length)} documents uploaded`,
      );
      onUploadComplete();
    } catch (e) {
      // REVIEW-2 NF-4: surface user-initiated cancellation distinctly so the
      // toast/error message reads like an explicit action rather than a bug.
      if (controller.signal.aborted) {
        setError('Upload cancelled.');
      } else {
        const err = e instanceof ApiError ? e : null;
        const status = err?.status ?? 0;
        const detail = err?.detail ?? err?.message ?? 'Upload failed.';
        setError(
          status === 415
            ? 'Server rejected the file type.'
            : status === 413
              ? 'Server rejected the file (too large).'
              : detail,
        );
      }
    } finally {
      setBusy(false);
      // Clear progress after a short delay so the user briefly sees 100%.
      setProgress(null);
      if (inputRef.current !== null) {
        inputRef.current.value = '';
      }
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  }

  function onChange(e: ChangeEvent<HTMLInputElement>): void {
    void handleFiles(e.target.files);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (!canUpload) return;
    void handleFiles(e.dataTransfer.files);
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>): void {
    if (!canUpload) return;
    e.preventDefault();
  }

  async function handleDownload(doc: LogsheetDocument): Promise<void> {
    if (entryId === null) return;
    try {
      const blob = await downloadLogsheetDocumentRequest(entryId, doc.id);
      downloadBlob(blob, doc.fileName);
    } catch (e) {
      const err = e instanceof ApiError ? e : null;
      toast.error('Could not download', {
        description: err?.detail ?? err?.message ?? 'Download failed.',
      });
    }
  }

  async function handleRemove(doc: LogsheetDocument): Promise<void> {
    if (entryId === null) return;
    try {
      await deleteLogsheetDocumentRequest(entryId, doc.id);
      toast.success('Document removed');
      onUploadComplete();
    } catch (e) {
      const err = e instanceof ApiError ? e : null;
      toast.error('Could not remove document', {
        description: err?.detail ?? err?.message ?? 'Remove failed.',
      });
    }
  }

  return (
    <div className="space-y-3" data-testid="document-upload-zone">
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        className={
          'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-input p-6 text-center text-sm' +
          (canUpload ? '' : ' opacity-60')
        }
      >
        <UploadSimpleIcon size={28} weight="duotone" />
        {entryId === null ? (
          <p className="text-muted-foreground" data-testid="doc-upload-needs-save">
            Save draft first to upload documents.
          </p>
        ) : !canUpload ? (
          <p className="text-muted-foreground" data-testid="doc-upload-disabled">
            Documents can only be uploaded while the entry is in Draft status.
          </p>
        ) : (
          <>
            <p>Drag a file here or</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                inputRef.current?.click();
              }}
              data-testid="doc-upload-button"
              disabled={busy}
            >
              {busy ? 'Uploading…' : 'Choose file'}
            </Button>
            <p className="text-xs text-muted-foreground">PDF, JPEG, or PNG up to 10 MB.</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_MIMES.join(',')}
          className="hidden"
          onChange={onChange}
          data-testid="doc-upload-input"
        />
      </div>

      {progress !== null && (
        <div
          className="flex items-center gap-3 text-xs text-muted-foreground"
          data-testid="doc-upload-progress"
        >
          <span>Uploading… {progress}%</span>
          {busy && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCancelUpload}
              data-testid="doc-upload-cancel"
              aria-label="Cancel upload"
            >
              Cancel
            </Button>
          )}
        </div>
      )}
      {error !== null && (
        <p className="text-xs text-destructive" data-testid="doc-upload-error">
          {error}
        </p>
      )}

      {documents.length > 0 && (
        <Table data-testid="doc-list-table">
          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc.id} data-testid={`doc-row-${String(doc.id)}`}>
                <TableCell className="font-medium" data-testid={`doc-row-${String(doc.id)}-name`}>
                  {doc.fileName}
                </TableCell>
                <TableCell className="font-mono text-xs">{formatBytes(doc.sizeBytes)}</TableCell>
                <TableCell className="font-mono text-xs">
                  {new Date(doc.uploadedAt).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        void handleDownload(doc);
                      }}
                      data-testid={`doc-download-${String(doc.id)}`}
                      aria-label={`Download ${doc.fileName}`}
                    >
                      <DownloadSimpleIcon size={14} weight="bold" />
                    </Button>
                    {canRemove && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          void handleRemove(doc);
                        }}
                        data-testid={`doc-remove-${String(doc.id)}`}
                        aria-label={`Remove ${doc.fileName}`}
                      >
                        <TrashIcon size={14} weight="bold" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
